import { FormikErrors } from 'formik';
import { ValidationError } from 'yup';

// Yup's own `AnySchema` is not exported in a usable shape here; what this needs
// of a schema is the one call below.
interface ValidatableSchema {
    validateSync: (values: unknown, options: { abortEarly: boolean }) => unknown;
}

/**
 * What a tab's own rules find in values the tab never showed.
 *
 * A mask reports a tab as valid only once that tab's form has been mounted and
 * has run Formik — so a required field on a tab nobody opened kept its initial
 * "valid", and the record saved without it (#2212, R-28: a supplier without a
 * Gegenkonto, a member without Lieferbedingungen). The values are in the store
 * either way; this runs the same schema over them from outside the form.
 *
 * Shaped like Formik's errors, so it drops straight into
 * `paloMissingFieldsMessage` beside the errors a mounted form reported.
 */
export const paloSchemaErrors = (schema: ValidatableSchema, values: unknown): FormikErrors<unknown> => {
    if (!values) {
        return {};
    }

    try {
        schema.validateSync(values, { abortEarly: false });

        return {};
    } catch (error) {
        if (!(error instanceof ValidationError)) {
            throw error;
        }

        // `inner` holds one entry per failing field; a schema that stops at the
        // first one reports itself instead, and then its own path is all there is.
        const failures = error.inner.length > 0 ? error.inner : [error];

        return failures.reduce<FormikErrors<unknown>>(
            (all, failure) => (failure.path ? { ...all, [failure.path]: failure.message } : all),
            {}
        );
    }
};
